import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Layers, Zap, HelpCircle } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';
import TapSparkline from '@/components/TapSparkline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TechnicalsAndShortDataCards — Round 95c (Jun 2026).
 *
 * Three speculative-sentiment cards extracted from `MassiveInsightsTab`
 * so they can live where they belong: the Stock Metrics tab. The user
 * called these out as out-of-place under "Insights" — a dividend-only
 * platform shouldn't put SMA50 / RSI14 / short-volume next to
 * fund-overview material.
 *
 * Cards (each self-hides when Massive returns empty data):
 *   • Technicals — SMA50 + RSI14 with Overbought / Oversold / Neutral
 *   • Short Interest & Float — settlement date + days-to-cover + % of float
 *   • Short Volume (daily FINRA) — ratio coloured rose when > 50%
 */

const fmtBig = (n) => {
  if (n == null) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return `${n}`;
};

function SectionHeader({ icon: Icon, title, hint }) {
  return (
    <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
      <div className="flex items-center gap-1.5">
        {Icon ? <Icon className="w-4 h-4 text-fuchsia-500 dark:text-fuchsia-400" /> : null}
        <h4 className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400">
          {title}
        </h4>
      </div>
      {hint && <span className="text-[10px] font-bold text-muted-foreground">{hint}</span>}
    </div>
  );
}

export default function TechnicalsAndShortDataCards({ ticker, navigateWithOrigin }) {
  // Round 95y (Jun 2026) — `technicals` state removed; the Stock
  // Metrics tab now has a chart-linked Technicals card that lives
  // under the price chart and mirrors the overlay toggles.
  const [shortInt, setShortInt] = useState(null);
  const [floatData, setFloatData] = useState(null);
  const [shortVol, setShortVol] = useState(null);

  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/stocks/${ticker}/short-interest`, { withCredentials: true }).catch(() => ({ data: { short_interest: null } })),
      axios.get(`${API}/stocks/${ticker}/float`, { withCredentials: true }).catch(() => ({ data: { float_shares: null } })),
      axios.get(`${API}/stocks/${ticker}/short-volume`, { withCredentials: true }).catch(() => ({ data: { short_volume: null } })),
    ]).then(([s, f, sv]) => {
      if (cancelled) return;
      setShortInt(s.data?.short_interest ? s.data : null);
      setFloatData(f.data?.float_shares ? f.data : null);
      setShortVol(sv.data?.short_volume ? sv.data : null);
    });
    return () => { cancelled = true; };
  }, [ticker]);

  const siPctFloat = (shortInt?.short_interest && floatData?.float_shares)
    ? (shortInt.short_interest / floatData.float_shares) * 100
    : (shortInt?.short_pct_float != null ? shortInt.short_pct_float * 100 : null);

  // Round P147 (Jun 2026) — change vs prior settlement, computed from
  // the bi-weekly history array (last two points).
  const siDelta = (() => {
    const h = shortInt?.history;
    if (!h || h.length < 2) return null;
    const prev = h[h.length - 2];
    const curr = h[h.length - 1];
    if (!prev?.short_interest || curr?.short_interest == null) return null;
    const pct = ((curr.short_interest - prev.short_interest) / prev.short_interest) * 100;
    const prevLabel = (prev.settlement_date || '').slice(5).replace('-', '/');
    return { pct, prevLabel, rising: pct > 0 };
  })();

  const isTrueFloat = floatData?.float_source === 'free_float';

  // Round 96n (Jul 3 2026) — Squeeze verdict badge. Thresholds match
  // the guide `/learn/short-squeeze-dynamics`:
  //   score = SI-of-float × days-to-cover
  //   ≥200 Extreme · ≥100 Elevated · ≥40 Moderate · else Low
  const score = (siPctFloat != null && shortInt?.days_to_cover)
    ? siPctFloat * shortInt.days_to_cover
    : null;
  let verdict = null;
  let verdictCls = '';
  if (score != null) {
    if (score >= 200) { verdict = 'Extreme'; verdictCls = 'bg-rose-100 dark:bg-rose-950/40 text-rose-800 dark:text-rose-200 border-rose-300/60 dark:border-rose-800/60'; }
    else if (score >= 100) { verdict = 'Elevated'; verdictCls = 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 border-amber-300/60 dark:border-amber-800/60'; }
    else if (score >= 40) { verdict = 'Moderate'; verdictCls = 'bg-sky-100 dark:bg-sky-950/40 text-sky-800 dark:text-sky-200 border-sky-300/60 dark:border-sky-800/60'; }
    else { verdict = 'Low'; verdictCls = 'bg-muted/40 text-muted-foreground border-border/60'; }
  }

  if (!shortInt && !floatData && !shortVol) return null;

  return (
    <div className="space-y-3" data-testid="technicals-short-data-cards">
      {/* Round 95y (Jun 2026) — The dedicated Technicals card was
          removed from this group because StockMetricsTab.js now
          renders a chart-linked `TechnicalsOverlayCard` directly
          under the price chart. That card mirrors the overlay
          toggles (SMA20/50/200, RSI14) and shows live values for
          whatever overlays are currently active. The duplicate
          static "50-day SMA · 14-day RSI" card here was confusing
          users about which one was authoritative. Short
          Interest/Float and Short Volume cards still render below. */}

      {(shortInt || floatData) && (
        <div className="rounded-lg border bg-card p-3" data-testid="metrics-short-interest-card">
          <SectionHeader
            icon={AlertTriangle}
            title="Short Interest & Float"
            hint={shortInt?.settlement_date ? `Settlement ${shortInt.settlement_date}` : undefined}
          />
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
            <div>
              <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide" title={shortInt?.source === 'yahoo' ? 'Massive has no FINRA record for this ticker — value from Yahoo backup.' : undefined}>
                Short Interest{shortInt?.source === 'yahoo' ? ' *' : ''}
              </div>
              <div className="font-mono font-bold">{shortInt?.short_interest != null ? fmtBig(shortInt.short_interest) : '—'}</div>
              {siDelta && (
                <div
                  className={`text-[10px] font-mono font-semibold ${siDelta.rising ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}
                  title={`Change vs the prior bi-weekly FINRA settlement (${siDelta.prevLabel}). Rising short interest = more bearish positioning.`}
                  data-testid="metrics-si-delta"
                >
                  {siDelta.rising ? '▲' : '▼'} {siDelta.pct >= 0 ? '+' : ''}{siDelta.pct.toFixed(1)}% vs {siDelta.prevLabel}
                </div>
              )}
            </div>
            <div>
              <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide">Days-to-Cover</div>
              <div className="font-mono font-bold">{shortInt?.days_to_cover != null ? shortInt.days_to_cover.toFixed(2) : '—'}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide" title="Average daily trading volume used by FINRA to compute days-to-cover.">Avg Daily Vol</div>
              <div className="font-mono font-bold">{shortInt?.avg_daily_volume != null ? fmtBig(shortInt.avg_daily_volume) : '—'}</div>
            </div>
            <div>
              <div
                className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide"
                title={isTrueFloat
                  ? `True free float from Massive — shares genuinely available for public trading (excludes insiders, 5%+ holders, lock-ups).${floatData?.float_effective_date ? ` Effective ${floatData.float_effective_date}.` : ''}`
                  : floatData?.float_source === 'yahoo'
                    ? 'Massive has no float figure for this ticker — value from Yahoo backup.'
                    : 'Weighted shares outstanding — a float proxy used when the true free-float figure is not yet published for this ticker.'}
              >
                {isTrueFloat ? 'Free Float' : floatData?.float_source === 'yahoo' ? 'Float *' : 'Float (proxy)'}
              </div>
              <div className="font-mono font-bold">{floatData?.float_shares != null ? fmtBig(floatData.float_shares) : '—'}</div>
              {floatData?.free_float_percent != null && (
                <div className="text-[10px] font-mono text-muted-foreground" title="Free float as % of total shares outstanding.">
                  {floatData.free_float_percent.toFixed(1)}% of shares
                </div>
              )}
            </div>
            <div>
              <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide" title={`Short interest as % of ${isTrueFloat ? 'true free float' : 'float'} — the squeeze gauge most traders watch.`}>% of Float</div>
              <div className={`font-mono font-bold ${siPctFloat != null ? 'text-rose-600 dark:text-rose-400' : ''}`} data-testid="metrics-si-pct-float">
                {siPctFloat != null ? `${siPctFloat.toFixed(2)}%` : '—'}
              </div>
            </div>
          </div>
          {/* Round 96n (Jul 3 2026) — Squeeze verdict badge + guide
              cross-link. Verdict thresholds mirror `services/
              squeeze_radar_warmer.py`. Guide link opens the
              educational primer on how squeezes work. */}
          {verdict && (
            <div className="mt-2.5 pt-2.5 border-t border-border/40 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${verdictCls}`} data-testid="squeeze-verdict-badge" title={`Score = ${siPctFloat.toFixed(1)}% × ${shortInt.days_to_cover.toFixed(1)}d = ${score.toFixed(1)}. Thresholds: Low <40 · Moderate 40-99 · Elevated 100-199 · Extreme 200+.`}>
                  <Zap className="w-3 h-3" />
                  Squeeze verdict: {verdict}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (typeof navigateWithOrigin === 'function') {
                    navigateWithOrigin('/learn/short-squeeze-dynamics');
                  } else {
                    window.location.href = '/learn/short-squeeze-dynamics';
                  }
                }}
                className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 cursor-pointer"
                data-testid="squeeze-guide-link"
                title="Open the Learn guide on how short squeezes work. Back button returns here with this stock modal reopened."
              >
                <HelpCircle className="w-3 h-3" />
                How squeezes work
              </button>
            </div>
          )}
          {/* Round 96n (Jul 3 2026) — 6-month SI history mini-chart.
              Bi-weekly settlement rows drawn as an SVG sparkline so
              users can spot rising/falling short-interest trends. */}
          {shortInt?.history && shortInt.history.length >= 3 && (
            <div className="mt-2.5 pt-2.5 border-t border-border/40" data-testid="si-history-mini-chart">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  6-Month SI Trend
                </span>
                <span className="text-[10px] text-muted-foreground/70 font-mono">
                  {shortInt.history.length} bi-weekly points
                </span>
              </div>
              <TapSparkline
                testId="si-history-sparkline"
                ariaLabel="Short-interest history sparkline"
                points={shortInt.history.map((p) => ({
                  label: p.settlement_date,
                  value: p.short_interest,
                  display: `${fmtBig(p.short_interest)} shares short`,
                }))}
              />
              <div className="flex items-center justify-between text-[10px] text-muted-foreground/70 font-mono mt-0.5">
                <span>{shortInt.history[0]?.settlement_date}</span>
                <span>{shortInt.history[shortInt.history.length - 1]?.settlement_date}</span>
              </div>
            </div>
          )}
          {/* Round P147 (Jun 2026) — provenance note so users don't
              assume the data is broken when a broker shows different
              numbers (brokers blend daily vendor estimates + their
              own borrow desk; FINRA settlements are the official
              record, published bi-weekly with ~9-day lag). */}
          <p className="mt-2 text-[10px] text-muted-foreground/70 leading-snug" data-testid="si-provenance-note">
            Official FINRA bi-weekly settlement — brokers may show daily vendor estimates that update between settlements.
          </p>
        </div>
      )}

      {shortVol?.short_volume_ratio != null && (
        <div className="rounded-lg border bg-card p-3" data-testid="metrics-short-volume-card">
          <SectionHeader
            icon={Layers}
            title={
              <AbbrTip term="FINRA_SHORT_VOLUME" className="!normal-case">
                Short Volume (Daily FINRA)
              </AbbrTip>
            }
            hint={shortVol.date ? `As of ${shortVol.date}` : undefined}
          />
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide">Short Vol</div>
              <div className="font-mono font-bold">{fmtBig(shortVol.short_volume)}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide">Total Vol</div>
              <div className="font-mono font-bold">{fmtBig(shortVol.total_volume)}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide">Ratio</div>
              <div className={`font-mono font-bold ${shortVol.short_volume_ratio > 0.5 ? 'text-rose-600 dark:text-rose-400' : shortVol.short_volume_ratio > 0.3 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                {(shortVol.short_volume_ratio * 100).toFixed(1)}%
              </div>
            </div>
          </div>
          {/* Round P147 (Jun 2026) — 30-day daily short-volume-ratio
              sparkline. FINRA short volume updates every trading day,
              so this is the daily-cadence sentiment view (the closest
              equivalent to a broker's daily short chart). */}
          {shortVol.history && shortVol.history.length >= 5 && (() => {
            const pts = shortVol.history.filter((p) => p.short_volume_ratio != null);
            if (pts.length < 5) return null;
            const values = pts.map((p) => p.short_volume_ratio);
            const min = Math.min(...values);
            const max = Math.max(...values);
            return (
              <div className="mt-2.5 pt-2.5 border-t border-border/40" data-testid="short-volume-mini-chart">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    30-Day Ratio Trend
                  </span>
                  <span className="text-[10px] text-muted-foreground/70 font-mono">
                    {pts.length} trading days · {(min * 100).toFixed(0)}–{(max * 100).toFixed(0)}%
                  </span>
                </div>
                <TapSparkline
                  testId="short-volume-sparkline"
                  ariaLabel="Daily short-volume ratio sparkline"
                  strokeClass="text-fuchsia-500 dark:text-fuchsia-400"
                  dotClass="fill-fuchsia-500 dark:fill-fuchsia-400"
                  points={pts.map((p) => ({
                    label: p.date,
                    value: p.short_volume_ratio,
                    display: `${(p.short_volume_ratio * 100).toFixed(1)}% short volume`,
                  }))}
                />
                <div className="flex items-center justify-between text-[10px] text-muted-foreground/70 font-mono mt-0.5">
                  <span>{pts[0]?.date}</span>
                  <span>{pts[pts.length - 1]?.date}</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
